import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Check, X } from 'lucide-react';
import { useMyTodos, useCreateTodo, useUpdateTodo } from '../../hooks/useMyHome';
import type { TodoItem } from '../../hooks/useMyHome';

// ── 상수 ──
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const PRIORITY_DOT: Record<string, string> = {
  HIGH: 'bg-red-500',
  MEDIUM: 'bg-yellow-500',
  LOW: 'bg-blue-500',
};

// ── 날짜 유틸 ──
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatMonth(d: Date): string {
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
}

function formatTime(dueTime: string | null): string {
  if (!dueTime) return '';
  const d = new Date(dueTime);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

function buildCalendarDays(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const days: Date[] = [];

  // 이전 달의 날짜로 첫 주 채우기
  for (let i = 0; i < first.getDay(); i++) {
    days.push(new Date(year, month, -first.getDay() + i + 1));
  }
  // 현재 달
  for (let d = 1; d <= last.getDate(); d++) {
    days.push(new Date(year, month, d));
  }
  // 다음 달로 마지막 주 채우기 (6줄 = 42칸)
  while (days.length < 42) {
    const nextDay = days.length - first.getDay() - last.getDate() + 1;
    days.push(new Date(year, month + 1, nextDay));
  }

  return days;
}

// ── 날짜 셀 팝오버 ──
function DayPopover({
  date,
  todos,
  onClose,
  onToggle,
  onAdd,
}: {
  date: Date;
  todos: TodoItem[];
  onClose: () => void;
  onToggle: (id: string, completed: boolean) => void;
  onAdd: (dateStr: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const dateStr = toDateKey(date);
  const label = `${date.getMonth() + 1}/${date.getDate()}`;

  return (
    <div
      ref={ref}
      className="absolute z-30 top-full left-1/2 -translate-x-1/2 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-700">{label}</span>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="size-3.5" />
        </button>
      </div>

      {todos.length === 0 ? (
        <p className="text-[11px] text-gray-400 py-2 text-center">이 날짜에 할 일이 없습니다</p>
      ) : (
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {todos.map((t) => (
            <div key={t.id} className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => onToggle(t.id, !t.completed)}
                className={`size-3.5 rounded border flex items-center justify-center shrink-0 ${
                  t.completed ? 'bg-primary-500 border-primary-500 text-white' : 'border-gray-300'
                }`}
              >
                {t.completed && <Check className="size-2.5" />}
              </button>
              <span className={`text-[11px] truncate flex-1 ${t.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                {t.title}
              </span>
              {t.dueTime && <span className="text-[11px] text-gray-400 shrink-0">{formatTime(t.dueTime)}</span>}
              <span className={`size-1.5 rounded-full shrink-0 ${PRIORITY_DOT[t.priority]}`} />
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => onAdd(dateStr)}
        className="flex items-center gap-0.5 mt-2 px-2 py-1 w-full text-[11px] text-primary-600 hover:bg-primary-50 rounded transition-colors justify-center"
      >
        <Plus className="size-3" /> 추가
      </button>
    </div>
  );
}

// ── 인라인 추가 팝오버 ──
function InlineAddPopover({
  dateStr,
  onClose,
}: {
  dateStr: string;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const createTodo = useCreateTodo();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleCreate = () => {
    if (!title.trim() || createTodo.isPending) return;
    createTodo.mutate({ title: title.trim(), dueDate: dateStr });
    onClose();
  };

  return (
    <div className="absolute z-40 top-full left-1/2 -translate-x-1/2 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 p-3">
      <p className="text-[11px] text-gray-500 mb-1">{dateStr} 할 일 추가</p>
      <div className="flex gap-1">
        <input
          ref={inputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleCreate();
            if (e.key === 'Escape') onClose();
          }}
          className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
          placeholder="제목 입력 후 Enter"
        />
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── 메인 컴포넌트 ──
export default function TodoCalendarView() {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [addingDate, setAddingDate] = useState<string | null>(null);

  const updateTodo = useUpdateTodo();

  // API 호출: 현재 월의 from/to
  const from = toDateKey(startOfMonth(currentMonth));
  const to = toDateKey(endOfMonth(currentMonth));
  const { data: todos } = useMyTodos({ view: 'calendar', from, to });

  // 날짜별 그룹핑
  const todosByDate = useMemo(() => {
    const map = new Map<string, TodoItem[]>();
    if (!todos) return map;
    for (const t of todos) {
      // dueDate 또는 schedule.startTime 기준
      let key: string | null = null;
      if (t.dueDate) {
        key = t.dueDate.slice(0, 10);
      } else if (t.schedule?.startTime) {
        key = t.schedule.startTime.slice(0, 10);
      }
      if (key) {
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(t);
      }
    }
    return map;
  }, [todos]);

  const calendarDays = useMemo(
    () => buildCalendarDays(currentMonth.getFullYear(), currentMonth.getMonth()),
    [currentMonth],
  );

  const handlePrevMonth = () =>
    setCurrentMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const handleNextMonth = () =>
    setCurrentMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const handleToday = () =>
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));

  const handleToggle = useCallback(
    (id: string, completed: boolean) => {
      updateTodo.mutate({ todoId: id, completed });
    },
    [updateTodo],
  );

  const handleAddFromPopover = useCallback((dateStr: string) => {
    setSelectedDate(null);
    setAddingDate(dateStr);
  }, []);

  return (
    <div>
      {/* 네비게이션 */}
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={handlePrevMonth} className="p-1 text-gray-400 hover:text-gray-600 rounded">
          <ChevronLeft className="size-4" />
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">{formatMonth(currentMonth)}</span>
          <button
            type="button"
            onClick={handleToday}
            className="px-1.5 py-0.5 text-[11px] text-primary-600 hover:bg-primary-50 rounded transition-colors"
          >
            오늘
          </button>
        </div>
        <button type="button" onClick={handleNextMonth} className="p-1 text-gray-400 hover:text-gray-600 rounded">
          <ChevronRight className="size-4" />
        </button>
      </div>

      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((w, i) => (
          <div
            key={w}
            className={`text-center text-[11px] font-medium py-1 ${
              i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400'
            }`}
          >
            {w}
          </div>
        ))}
      </div>

      {/* 캘린더 그리드 */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, idx) => {
          const key = toDateKey(day);
          const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
          const isToday = isSameDay(day, today);
          const dayTodos = todosByDate.get(key) ?? [];
          const isSelected = selectedDate && isSameDay(day, selectedDate);

          // 우선순위별 점 (최대 3개, 중복 제거)
          const priorityDots = [...new Set(dayTodos.map((t) => t.priority))].slice(0, 3);
          // allDay 항목
          const allDayItems = dayTodos.filter((t) => t.schedule?.isAllDay);

          return (
            <div key={idx} className="relative">
              <button
                type="button"
                onClick={() => {
                  setAddingDate(null);
                  setSelectedDate(isSelected ? null : day);
                }}
                className={`w-full aspect-square flex flex-col items-center justify-start pt-1 text-[11px] rounded-md transition-colors relative ${
                  !isCurrentMonth ? 'text-gray-300' : isToday ? 'bg-primary-50 text-primary-700 font-bold' : 'text-gray-700 hover:bg-gray-50'
                } ${isSelected ? 'ring-1 ring-primary-400' : ''}`}
              >
                <span>{day.getDate()}</span>

                {/* allDay 바 */}
                {allDayItems.length > 0 && (
                  <div className="w-4/5 h-0.5 bg-primary-400 rounded-full mt-0.5" />
                )}

                {/* 우선순위 점 */}
                {priorityDots.length > 0 && (
                  <div className="flex gap-0.5 mt-0.5">
                    {priorityDots.map((p) => (
                      <span key={p} className={`size-1 rounded-full ${PRIORITY_DOT[p]}`} />
                    ))}
                  </div>
                )}
              </button>

              {/* 날짜 팝오버 */}
              {isSelected && (
                <DayPopover
                  date={day}
                  todos={dayTodos}
                  onClose={() => setSelectedDate(null)}
                  onToggle={handleToggle}
                  onAdd={handleAddFromPopover}
                />
              )}

              {/* 인라인 추가 팝오버 */}
              {addingDate === key && (
                <InlineAddPopover
                  dateStr={key}
                  onClose={() => setAddingDate(null)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
